import React, {memo} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import uploadApi from '../../../service/uploadApi';
import {MinWidthButtonWrapper, StyledAnchor} from '../../util/StyledElements';
import Axios, {AxiosProgressEvent, CancelTokenSource} from 'axios';
import '../../../static/css/modal.less';
import cssConstants from '../../../constants/cssConstants';
import WarningWithFaqLink from '../../util/WarningWithFaqLink';
import {IFileUploadCandidate} from '../../../model/fileUploadCandidate';
import {useTscTheme} from '../../../context/themeProvider';
import {AlertModalPortal, BasicModalPortal} from '../../util/ModalPortalHelper';
import UploadProgressModal from '../ReplaceMediaModal/UploadProgressModal';
import LocalMediaImporter from '../ReplaceMediaModal/LocalMediaImporter';
import config from '../../../service/config';
import userApi from '../../../service/userApi';
import Constants from '../../../constants/Constants';

const MediaPreviewWrapper = styled.div`
   display: flex;
   object-fit: contain;
   justify-content: center;
   max-height: 30rem;
   max-width: 30rem;
   margin-bottom: 1rem;
`;

export const VideoPreview = styled.video`
   max-height: ${cssConstants.defaultModalWidth};
   max-width: ${cssConstants.defaultModalWidth};
   height: 100%;
   width: 100%;
`;

export const ImagePreview = styled.img`
   max-height: ${cssConstants.defaultModalWidth};
   max-width: ${cssConstants.defaultModalWidth};
   height: 100%;
   width: 100%;
   background-color: ${cssConstants.headerBackgroundColor};
`;

const MediaRequirementsAnchor = styled(StyledAnchor)`
   font-size: 0.875rem;
   align-self: center;
`;

const ButtonGroup = styled.div<{fileSelected: boolean}>`
   margin-top: 1rem;
   width: 100%;
   display: flex;
   justify-content: ${props => props.fileSelected ? 'flex-end' : 'space-between'};

   button {
      margin-left: 0.5rem;
   }
`;

const DetailText = styled.div`
   margin-bottom: 1rem;
`;

export const UploadLogoModalBase: React.FC<IUploadLogoModalProps> = ({onClose}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [state, setState] = React.useState({
      cancelTokenSource: null,
      errorText: '',
      file: null,
      fileSelected: false,
      fileUploading: false,
      showReplaceConfirmationModal: false,
      showRemoveConfirmationModal: false,
      showFileSelectionModal: true,
      showUploadProgressModal: false,
      uploadProgress: 0
   } as IUploadLogoModalState);

   const updateState = (updatedValues: Partial<IUploadLogoModalState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const onUploadProgress = (progressEvent: AxiosProgressEvent): void => {
      const near100Percent = 95;
      const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), near100Percent);
      updateState({uploadProgress: percentCompleted});
   };

   const onFileSelect = (uploadCandidate: IFileUploadCandidate) => {
      updateState({fileSelected: true, file: uploadCandidate.file});
   };

   const uploadLogo = async (): Promise<void> => {
      try {
         const cancelTokenSource = Axios.CancelToken.source();
         updateState({cancelTokenSource: cancelTokenSource});
         const storageFileId: string = await uploadApi.uploadCustomLogo(state.file, onUploadProgress, cancelTokenSource);
         await userApi.upsertCustomLogoStorageFileId(storageFileId);
         updateState({uploadProgress: 100, fileUploading: false});
         window.location.reload();
      } catch (e) {
         let errorText = '';
         if (!Axios.isCancel(e)) {
            if (e.status === Constants.statusCodes.unsupportedMediaType) {
               if (e?.response?.data?.detail?.includes('MaxHeight') || e?.response?.data?.detail?.includes('MaxWidth')) {
                  errorText = t('uploadCustomLogo.invalidDimensionsError');
               } else {
                  errorText = t('uploadMedia.fileTypeWarningCustomLogo');
               }
            } else {
               errorText = t('general.errorText');
            }
         }
         updateState({showReplaceConfirmationModal: false, fileUploading: false, uploadProgress: 0, errorText: errorText, showUploadProgressModal: false, showFileSelectionModal: true});
      }
   };

   const uploadLogoClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (state.file && state.fileSelected && config.environmentData.CustomSiteLogoUrl) {
         updateState({showReplaceConfirmationModal: true});
      } else {
         updateState({showReplaceConfirmationModal: false, showUploadProgressModal: true, showFileSelectionModal: false, fileUploading: true});
         await uploadLogo();
      }
   };

   const replaceConfirmClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      updateState({showReplaceConfirmationModal: false, showUploadProgressModal: true, showFileSelectionModal: false, fileUploading: true});
      await uploadLogo();
   };

   const onCancelClick = (): void => {
      if (state.file && state.fileSelected) {
         updateState({file: null, fileSelected: false, errorText: ''});
      } else {
         onClose();
      }
   };

   const onReplaceClick = (): void => {
      if (state.file && state.fileSelected) {
         updateState({showReplaceConfirmationModal: true});
      } else {
         onClose();
      }
   };

   const removeLogo = async () => {
      await userApi.removeCustomLogoStorageFileId();
      window.location.reload();
   };

   const onResetToDefaultClick = (): void => {
      updateState({showRemoveConfirmationModal: true});
   };

   const onResetToDefaultConfirmClick = async () => {
      updateState({showFileSelectionModal: false, showRemoveConfirmationModal: false});
      await removeLogo();
   };

   const onFileSelectionModalClose = (): void => {
      updateState({showFileSelectionModal: false, showUploadProgressModal: state.fileUploading});
      !state.fileUploading && onClose();
   };

   const onConfirmationModalClose = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      e.stopPropagation();
      updateState({showRemoveConfirmationModal: false,
         showReplaceConfirmationModal: false});
   };

   const onUploadProgressModalClose = (): void => {
      if (!state.fileUploading && state.uploadProgress === 100) {
         onClose();
      } else {
         updateState({showUploadProgressModal: false, showFileSelectionModal: true});
      }
   };

   const handleUploadCancelRequest = (): void => {
      state.cancelTokenSource?.cancel();
   };

   const buttonGroup: React.JSX.Element = (
      <ButtonGroup fileSelected={state.fileSelected}>
         {!state.fileSelected && (
            <MediaRequirementsAnchor
               href={Constants.navigation.customLogoMediaFAQLink}
               target="_blank"
               rel="noopener noreferrer nofollow"
            >
               {t('uploadCustomLogo.mediaGuidelines')}
            </MediaRequirementsAnchor>
         )}
         <MinWidthButtonWrapper>
            {config.environmentData.CustomSiteLogoUrl && !state.fileSelected && (
               <ControlButton
                  label={t('uploadCustomLogo.resetToDefault')}
                  buttonType={TscButtonType.secondary}
                  disabled={false}
                  themeName={theme}
                  onClick={onResetToDefaultClick}
                  testId="upload-custom-logo-modal-reset-to-default"
               />
            )}
            <ControlButton
               label={state.fileSelected ? t('general.back') : t('general.close')}
               buttonType={TscButtonType.secondary}
               disabled={false}
               themeName={theme}
               onClick={onCancelClick}
               testId="upload-custom-logo-modal-close-button"
            />
         </MinWidthButtonWrapper>
         {state.fileSelected && (
            <ControlButton
               label={config.environmentData.CustomSiteLogoUrl ? t('uploadCustomLogo.uploadImage') : t('uploadCustomLogo.uploadImage')}
               buttonType={TscButtonType.primary}
               disabled={!state.fileSelected}
               themeName={theme}
               onClick={config.environmentData.CustomSiteLogoUrl ? onReplaceClick : uploadLogoClick}
               testId="upload-custom-logo-modal-upload-image-button"
            />
         )}
      </ButtonGroup>
   );

   const mediaPreview: React.JSX.Element = state.file && (
      <MediaPreviewWrapper>
         <ImagePreview src={URL.createObjectURL(state.file)} />
      </MediaPreviewWrapper>
   );

   const fileSelectModal: React.JSX.Element = (
      <BasicModalPortal
         visible={true}
         title={config.environmentData.CustomSiteLogoUrl ? t('uploadCustomLogo.editCustomLogo') : t('uploadCustomLogo.action')}
         onClose={onFileSelectionModalClose}
         themeName={theme}
         testId="custom-logo-modal"
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               {!state.fileSelected && (
                  <DetailText>
                     <div>
                        {t('uploadCustomLogo.personalizeMessage')}
                     </div>
                     <div>
                        {t('uploadCustomLogo.sizeMessage')}
                     </div>
                  </DetailText>
               )}
               {state.errorText && (
                  <WarningWithFaqLink linkUrl={Constants.navigation.customLogoMediaFAQLink}>
                     {state.errorText}
                  </WarningWithFaqLink>
               )}
               {!state.fileSelected && (
                  <LocalMediaImporter
                     onFileSelect={onFileSelect}
                     allowMultiple={false}
                     ignoreQuotaLimits={true}
                     isLogoUploader={true}
                  />
               )}
               {(state.fileSelected && !state.fileUploading) && mediaPreview}
               {buttonGroup}
            </div>
         </div>
      </BasicModalPortal>
   );

   const replaceConfirmationAlertModal: React.JSX.Element = (
      <AlertModalPortal
         title={t('uploadCustomLogo.replaceLogoTitle')}
         onClose={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         onConfirm={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => replaceConfirmClick(e)}
         confirmButtonType={TscButtonType.primary}
         confirmContent={t('uploadCustomLogo.replace')}
         onDismiss={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.cancel')}
      >
         {t('uploadCustomLogo.replaceLogoConfirmation')}
      </AlertModalPortal>
   );

   const removeConfirmationAlertModal: React.JSX.Element = (
      <AlertModalPortal
         title={t('uploadCustomLogo.removeLogoTitle')}
         onClose={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         onConfirm={onResetToDefaultConfirmClick}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t('uploadCustomLogo.remove')}
         onDismiss={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.cancel')}
      >
         {t('uploadCustomLogo.removeLogoConfirmation')}
      </AlertModalPortal>
   );

   return (
      <>
         {state.showFileSelectionModal && fileSelectModal}
         {state.showReplaceConfirmationModal && replaceConfirmationAlertModal}
         {state.showRemoveConfirmationModal && removeConfirmationAlertModal}
         {state.showUploadProgressModal && (
            <UploadProgressModal
               uploadProgress={state.uploadProgress}
               cancelUpload={handleUploadCancelRequest}
               onClose={onUploadProgressModalClose}
            />
         )}
      </>
   );
};

export interface IUploadLogoModalProps {
   onClose(): void;
}

export interface IUploadLogoModalState {
   cancelTokenSource: CancelTokenSource;
   errorText: string;
   file: File;
   fileSelected: boolean;
   fileUploading: boolean;
   showReplaceConfirmationModal: boolean;
   showRemoveConfirmationModal: boolean;
   showFileSelectionModal: boolean;
   showUploadProgressModal: boolean;
   uploadProgress: number;
}

export default memo(UploadLogoModalBase);
